import { QuestionType } from '../../../../models/assignments';
import { OptionRequest } from '../../../../api/assignments/dto/QuestionRequests.dto';

// Черновик вопроса задания для клиентской валидации перед отправкой на сервер.
export interface QuestionDraft {
  type: QuestionType;
  question_text: string;
  options: OptionRequest[];
}

// Типы вопросов с вариантами ответа (single_choice / multiple_choice).
export function isChoiceType(type: QuestionType): boolean {
  return type === 'single_choice' || type === 'multiple_choice';
}

// Зеркало серверной validateQuestion из бэка (pkg/service/assignment.go) —
// клиентская валидация вопроса перед отправкой запроса, чтобы не ждать 400 от сервера.
// Возвращает русский текст первой найденной ошибки или null, если вопрос валиден.
export function validateQuestion(q: QuestionDraft): string | null {
  if (q.question_text.trim() === '') {
    return 'Введите текст вопроса';
  }

  if (isChoiceType(q.type)) {
    if (q.options.length < 2) {
      return 'Добавьте минимум два варианта';
    }

    if (q.options.some((option) => option.option_text.trim() === '')) {
      return 'Заполните текст всех вариантов';
    }

    const correctCount = q.options.filter((option) => option.is_correct).length;

    if (q.type === 'single_choice' && correctCount !== 1) {
      return 'Отметьте ровно один верный вариант';
    }

    if (q.type === 'multiple_choice' && correctCount < 1) {
      return 'Отметьте хотя бы один верный вариант';
    }

    return null;
  }

  if (q.options.length > 0) {
    return 'У этого типа вопроса не должно быть вариантов';
  }

  return null;
}

// Защита при смене типа вопроса в UI: сервер вернёт 400 на непустые options
// у free_text/file_upload, поэтому очищаем их на клиенте до отправки.
// Для single_choice дополнительно гарантируем, что верным отмечен только
// первый подходящий вариант (иначе после переключения multiple → single
// в стейте может остаться несколько is_correct: true).
export function normalizeQuestionForType<T extends QuestionDraft>(q: T): T {
  if (!isChoiceType(q.type)) {
    return { ...q, options: [] };
  }

  if (q.type === 'single_choice') {
    let firstCorrectFound = false;
    const options = q.options.map((option) => {
      if (!option.is_correct) {
        return option;
      }

      if (firstCorrectFound) {
        return { ...option, is_correct: false };
      }

      firstCorrectFound = true;
      return option;
    });

    return { ...q, options };
  }

  return q;
}
